import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { Input, Select, Spin, Checkbox, Button, Modal } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import AllAppRoutes from "../common/routes";
import { ReactComponent as WhatsAppIcon } from "../assets/images/icons/whatsappGreenEmoji.svg";
import {
  _create_business,
  _update_business,
  _upload_any_file,
  _get_cities_by_state_code,
  _get_available_features,
  _get_all_state,
  _get_profile,
} from "../common/axios_services";
import { setUserData } from "../slices/profileSlice";
import {
  showErrorNotification,
  showSuccessNotification,
} from "../common/utils/notification";
import AutoCompleteAddress from "./AutoCompleteAddress";
import { PinActivation } from "./PinActivation";

const { Option } = Select;

const schema = yup.object().shape({
  name: yup.string().required("Business name is required"),
  address: yup.string().required("Business address is required"),
  state: yup.string().required("State is required"),
  lga: yup.string().required("Location area is required"),
  featureIds: yup
    .array()
    .of(yup.string())
    .min(1, "At least one feature must be selected")
    .required("Features are required"),
});

const BusinessFormSection = ({ user }) => {
  const dispatch = useDispatch();
  const [states, setStates] = useState([]);
  const [lgas, setLgas] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingStates, setLoadingStates] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [selectedServices, setSelectedServices] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [showPinModal, setShowPinModal] = useState(false);
  const [onboardingErrorMessage, setOnboardingErrorMessage] = useState("");
  const [addressSelectedViaAutocomplete, setAddressSelectedViaAutocomplete] = useState(false);
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    setValue,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(schema),
  });

  const handleStateChange = async (state) => {
    try {
      const response = await _get_cities_by_state_code(state);
      setLgas(response.data.data);
    } catch (error) {
      showErrorNotification("Failed to load LGAs.");
      console.error("LGAs fetch error:", error);
      setLgas([]);
    }
    setValue("state", state);
  };

  useEffect(() => {
    const fetchData = async () => {
      setLoadingStates(true);
      try {
        const [featuresRes, statesRes] = await Promise.all([
          _get_available_features(),
          _get_all_state(),
        ]);
        setFeatures(featuresRes.data?.data || []);
        setStates(statesRes.data.data);
      } catch (error) {
        showErrorNotification("Failed to load data.");
        console.error("Data fetch error:", error);
      } finally {
        setLoading(false);
        setLoadingStates(false);
      }
    };
    fetchData();
  }, []);

  useEffect(() => {
    if (user) {
      if (user.business) {
        setValue("name", user.business.name);
        setValue("address", user.business.address);
        setValue("state", user.business.state);
        setValue("lga", user.business.lga);
        const preselectedFeatureIds = user.business.featureSubscriptions.map(
          (sub) => sub.featureId,
        );
        setValue("featureIds", preselectedFeatureIds);
        if (user.business.state) {
          handleStateChange(user.business.state);
        }
      } else {
        setValue("name", user.userName);
        setValue("address", user.address);
        setValue("state", user.state);
        setValue("lga", user.lga);
        if (user.state) {
          handleStateChange(user.state);
        }
      }
    }
  }, [user, setValue]);

  const antIcon = (
    <LoadingOutlined style={{ fontSize: 24, color: "#fff" }} spin />
  );

  if (loading) {
    return <Spin tip="Loading..." />;
  }

  const onSubmit = async (data) => {
    setSubmitting(true);
    try {
      const businessData = {
        name: data.name,
        address: data.address,
        state: data.state,
        lga: data.lga,
        featureIds: data.featureIds,
      };

      if (user?.business) {
        await _update_business(businessData);
        showSuccessNotification("Business updated successfully!");
        const profileResponse = await _get_profile();
        dispatch(setUserData(profileResponse.data?.data));
        navigate(AllAppRoutes.restaurant_profile);
      } else {
        await _create_business(businessData);
        showSuccessNotification("Business created successfully!");
        setShowModal(true);
      }
    } catch (error) {
      if (error.response?.status === 403 && error.response?.data?.message?.includes("verified users")) {
        setOnboardingErrorMessage(error.response.data.message);
        setShowPinModal(true);
      } else {
        showErrorNotification(error.response?.data?.message || "Failed to process business request.");
      }
      console.error("Business processing error:", error);
    } finally {
      setSubmitting(false);
    }
  };
  const formatFeatureLabel = (name) => {
    return name
      .split("_")
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(" ");
  };

  const missingDocs = [];

  return (
    <div className="pb-20">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Welcome to Konnect Business Portal
        </h1>
        <p className=" text-gray-600">
          Please fill the form below to activate your business
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <div>
          <label className="block  font-medium text-gray-900 mb-2">
            Business Name
          </label>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                placeholder="Enter business name"
                className="py-2"
              />
            )}
          />
          {errors.name && (
            <p className="text-red-500 text-xs mt-1">{errors.name.message}</p>
          )}
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block  font-medium text-gray-900 mb-2">
              State
            </label>
            <Controller
              name="state"
              control={control}
              render={({ field }) => (
                <Select
                  {...field}
                  onChange={handleStateChange}
                  placeholder="Select state"
                  disabled={loadingStates}
                >
                  {states.map((state) => (
                    <Option key={state.id} value={state.name}>
                      {state.name}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.state && (
              <p className="text-red-500 text-xs mt-1">
                {errors.state.message}
              </p>
            )}
          </div>
          <div>
            <label className="block  font-medium text-gray-900 mb-2">
              Location
            </label>
            <Controller
              name="lga"
              control={control}
              render={({ field }) => (
                <Select {...field} placeholder="Select location">
                  {lgas.map((lga) => (
                    <Option key={lga.id} value={lga.name}>
                      {lga.name}
                    </Option>
                  ))}
                </Select>
              )}
            />
            {errors.lga && (
              <p className="text-red-500 text-xs mt-1">{errors.lga.message}</p>
            )}
          </div>
        </div>

        <div>
          <label className="block  font-medium text-gray-900 mb-2">
            Address (include nearest bus-stop)
          </label>
          <Controller
            name="address"
            control={control}
            render={({ field }) => (
              <AutoCompleteAddress
                value={field.value}
                onChange={field.onChange}
                placeholder="Search for your business address..."
                onAddressSelect={(addressData) => {
                  setAddressSelectedViaAutocomplete(true);
                  field.onChange(
                    addressData.formattedAddress ||
                    addressData.address ||
                    field.value
                  );
                  if (addressData.state) {
                    handleStateChange(addressData.state);
                  }
                }}
              />
            )}
          />
          {addressSelectedViaAutocomplete && (
            <div
              style={{
                marginTop: 8,
                padding: 8,
                backgroundColor: "#e6f7ff",
                border: "1px solid #91d5ff",
                borderRadius: 4,
                fontSize: 12,
              }}
            >
              <span style={{ color: "#1890ff", fontWeight: "bold" }}>
                ✓ Address verified via Google Maps
              </span>
            </div>
          )}
          {errors.address && (
            <p className="text-red-500 text-xs mt-1">
              {errors.address.message}
            </p>
          )}
        </div>

        <div className="pt-4">
          <label className="block  font-bold text-gray-900 mb-3">
            Select Business Services
          </label>
          <Controller
            name="featureIds"
            control={control}
            render={({ field }) => (
              <Checkbox.Group
                options={
                  features &&
                  features?.map((feature) => ({
                    label: formatFeatureLabel(feature.name),
                    value: feature.id,
                  }))
                }
                value={field.value}
                onChange={(values) => {
                  field.onChange(values);
                  setSelectedServices(values || []);
                }}
                className="space-y-2 flex flex-col"
              />
            )}
          />
          {errors.featureIds && (
            <p className="text-red-500 text-xs mt-1">
              {errors.featureIds.message}
            </p>
          )}
        </div>

        <div className="flex gap-3 pt-4">
          <button disabled={submitting} type="submit" className="btn-primary">
            {submitting ? "Submitting..." : "Continue"}
          </button>
        </div>
      </form>

      <Modal
        title="Welcome to Konnect Business"
        open={showModal}
        onCancel={() => setShowModal(false)}
        footer={null}
        centered
      >
        <div className="space-y-4">
          <div className="flex justify-between items-start">
            <span className=" text-gray-700">Status:</span>
            <span className=" font-medium text-gray-900">
              Pending Verification
            </span>
          </div>
          <p className=" text-gray-700">
            Thank you for joining Konnect business. Our support team will engage
            with you within 24 hours.
          </p>
          {missingDocs.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
              <p className="text-xs text-blue-900">
                Please provide your compliance documentations -{" "}
                <span className="font-medium">{missingDocs.join(" & ")}</span>{" "}
                within 14 days
              </p>
            </div>
          )}
          <div className="pt-4 border-t border-gray-200 flex gap-3">
            <Button
              type="link"
              size="small"
              icon={<WhatsAppIcon style={{ width: 16, height: 16 }} />}
              href="https://wa.me/2348073376943"
              target="_blank"
              rel="noopener noreferrer"
              className="p-0 h-auto"
              style={{ color: "#1B75F0" }}
            >
              Fast track approval
            </Button>
            <button
              onClick={() => navigate(AllAppRoutes.restaurant_profile)}
              className="flex-1 px-4 py-2 bg-[#1B75F0] text-white font-medium rounded-md hover:opacity-90 transition-opacity "
            >
              Continue
            </button>
          </div>
        </div>
      </Modal>

      <PinActivation
        open={showPinModal}
        toClose={() => setShowPinModal(false)}
        accountId={user?.accountId}
        errorMessage={onboardingErrorMessage}
      />
    </div>
  );
};

export default BusinessFormSection;
